import {
  ExchangeRateName,
  EXCHANGE_RATE_NAME_TO_CURRENCY,
  RegularCurrencies,
  SWAP_MIN_AMOUNTS,
  SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT,
  SystemCurrency,
} from '@okletsplay/core';
import { number as numberSchema, object, string, TestFunction, TestContext } from 'yup';
import { AnyObject } from 'yup/lib/types';
import { UserBalanceDto } from 'app/interfaces/dto';
import { UserQuotaStatusDto } from 'app/interfaces/dto/user-quota-status.dto';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { quotasValidator } from '../validators';
import { QuotaOperation } from '../enums';

type Context = TestContext<AnyObject>;

const SOMETHING_WRONG_MESSAGE = 'Something went wrong, try later';

export const buildSwapFormSchema = (
  getUserAccounts: () => (UserBalanceDto | undefined)[],
  getQuotas: () => UserQuotaStatusDto | undefined
) => {
  const validateMinLimit = (value: number, sellingCurrency: SystemCurrency, context: Context) => {
    const minLimit = SWAP_MIN_AMOUNTS[sellingCurrency as RegularCurrencies];

    if (value < minLimit) {
      throw context.createError({
        message: `Minimum amount should be ${minLimit} ${sellingCurrency}`,
      });
    }
  };

  const validateQuotas = (value: number, pair: ExchangeRateName, context: Context) => {
    let mainCurrencyAmount: number;
    if (pair === ExchangeRateName.OKLP_USDT) {
      mainCurrencyAmount = value;
    } else if (pair === ExchangeRateName.USDT_OKLP) {
      mainCurrencyAmount = context.parent.buy;
    } else {
      throw context.createError({ message: SOMETHING_WRONG_MESSAGE });
    }

    quotasValidator({
      getQuotas,
      context,
      mainCurrencyAmount,
      quotaOperation: QuotaOperation.Swap,
    });
  };

  const validateUserBalance = (
    value: number,
    pair: ExchangeRateName,
    sellingCurrency: SystemCurrency,
    context: Context
  ) => {
    const userAccounts = getUserAccounts();
    const userBalance = userAccounts.find((balance) => balance?.currency === sellingCurrency);

    if (!userBalance) {
      throw context.createError({ message: SOMETHING_WRONG_MESSAGE });
    }

    const feePercent = SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT[pair];
    const amountWithFee = value + (value * feePercent) / 100;

    if (userBalance.balance < amountWithFee) {
      throw context.createError({
        message: `Not enough ${userBalance.currency} balance (+${feePercent}% platform fee)`,
      });
    }
  };

  const sellTestFunction: TestFunction<number | undefined> = (value, context) => {
    if (!value) {
      throw context.createError({ message: 'Provide an amount to swap' });
    }

    const pair = context.parent.pair as ExchangeRateName;
    const sellingCurrency = EXCHANGE_RATE_NAME_TO_CURRENCY[pair].sell;

    validateMinLimit(value, sellingCurrency, context);
    validateUserBalance(value, pair, sellingCurrency, context);

    if (MultiCurrencyConfig.isUserQuotaSwapEnabled()) {
      validateQuotas(value, pair, context);
    }

    return true;
  };

  return object().shape({
    buy: numberSchema().label('Buy Amount').required(),
    sell: numberSchema().label('Sell Amount').test(sellTestFunction),
    selectedSide: string().required().oneOf(['sellingCurrency', 'buyingCurrency']),
    pair: string().required().oneOf([ExchangeRateName.OKLP_USDT, ExchangeRateName.USDT_OKLP]),
  });
};
